import { Button } from "@/components/ui/button";
import { useLinkStrava } from "@/data/auth/hooks";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/strava/exchange_token")({
  component: RouteComponent,
  validateSearch: (search: Record<string, string>) => {
    return {
      code: search.code || "",
    };
  },
});

function RouteComponent() {
  const { code } = useSearch({ from: "/strava/exchange_token" });
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const linkStravaMutation = useLinkStrava();

  // Use a ref to track if the mutation has been called
  // Refs persist across renders without causing re-renders themselves
  const mutationCalledRef = useRef(false);

  useEffect(() => {
    // Only proceed if we have a code and haven't called the mutation yet
    if (!code) {
      setStatus("error");
      setErrorMessage("No authorization code provided");
      return;
    }
    
    if (mutationCalledRef.current) {
      return;
    }

    const linkAccount = async () => {
      // Mark that we've called the mutation before actually calling it
      // This prevents the mutation from being called twice in StrictMode
      // or if there are multiple re-renders
      mutationCalledRef.current = true;
      
      try {
        await linkStravaMutation.mutateAsync({ code });
        setStatus("success");
      } catch (error) {
        setStatus("error");
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to link Strava account"
        );
      }
    };

    linkAccount();
  }, [code, linkStravaMutation]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Strava Connection</h1>
          <p className="text-gray-500 mt-2">
            {status === "loading" && "Connecting your Strava account..."}
            {status === "success" &&
              "Your Strava account has been successfully connected!"}
            {status === "error" && "Failed to connect your Strava account"}
          </p>
        </div>

        <div className="flex justify-center py-8">
          {status === "loading" && (
            <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
          )}
          {status === "success" && (
            <CheckCircle className="h-16 w-16 text-green-500" />
          )}
          {status === "error" && <XCircle className="h-16 w-16 text-red-500" />}
        </div>

        {status === "error" && (
          <div className="p-4 bg-red-50 rounded-md">
            <p className="text-sm text-red-600">{errorMessage}</p>
          </div>
        )}

        <div className="pt-4">
          <Button
            className="w-full"
            onClick={() => navigate({ to: "/settings" })}
            variant={status === "error" ? "destructive" : "default"}
          >
            {status === "success" ? "Go to Settings" : "Back to Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
